import { v4 as uuid } from 'uuid';
import {
  ApiAssessmentResponse,
  ApiDiagramResponse,
  ApiLegacyCustomElementResponse,
  ApiStoryResponse,
} from '../../../../../api/core/models';
import { DEFAULT_TARGET_PRECISION } from '../../../../../app.constants';
import { getElementIconPath } from '../../../../../functions/transition-targets/get-element-details';
import { TrajectoryFunctionType } from '../../../../../functions/transition-targets/trajectory-function-type';
import { conditionallyPrefixImagesWithFilesUrl } from '../../../../../functions/utilities/img-url-utility';
import { ClientNode } from '../../../../../model/client-model/client-node';
import { ModelType } from '../../../../../model/client-model/properties/model-type';
import { NodeType } from '../../../../../model/client-model/properties/node-type';
import { CustomTargetModelData } from '../../../../../model/custom-model/custom-target-model-data';
import { TransitionElement } from '../../../../../model/enums/transition-element';
import { IndicatorImageSource } from '../../../../../model/indicator-image-source';
import { IndicatorListItem } from '../../../../../model/indicator-list-item';
import { PersistedTransitionTargetData } from '../../../../../model/transition-targets/persisted/persisted-transition-target-data';
import { storyHistoricalToHistoricalEmissionsData } from './story-historical-to-historical-emissions-data';

export const legacyCustomElementToClientNode = (
  group: ApiLegacyCustomElementResponse,
  boardId: string,
  baseUrlFiles?: string
): { node: ClientNode; indicator: IndicatorListItem } => {
  const nodeStory: ApiStoryResponse = group?.story ?? { id: group.id, title: '' };
  const historicalEmissionData = storyHistoricalToHistoricalEmissionsData(nodeStory?.historicalEmissions ?? {});
  const prefixIfSet = (url: string | undefined): string => {
    if (!url) {
      return '';
    }
    if (!baseUrlFiles) {
      return url;
    }
    return conditionallyPrefixImagesWithFilesUrl(url, baseUrlFiles);
  };
  const data = JSON.parse(group.data);
  const dataWithRequiredChartData = {
    chartData: { outcome: {}, emissions: {}, carbonAbatement: {}, target: {} },
    ...data,
  };
  const diagram: ApiDiagramResponse = data?.diagram ?? {};
  const assessment: ApiAssessmentResponse = data?.assessment ?? {};
  const indicatorId = uuid();
  const indicator: IndicatorListItem = {
    id: indicatorId,
    _etag: '',
    _ts: 0,
    title: '',
    indicatorProperties: {
      diff: assessment.data ?? 0,
      unknownDiff: assessment.isUnknown ?? false,
      assessmentDescription: assessment.about ?? '',
      potentialDescription: diagram.about ?? '',
      description: diagram.moreInfo ?? '',
      outcomeDescription: diagram.reference ?? '',
      goalDescription: data.target?.goalDescription ?? '',
      imgSource: IndicatorImageSource.Data,
    },
  };
  const transitionTargetData: PersistedTransitionTargetData = {
    transitionOutcome: { outcome: {} },
    transitionProperties: { targetLevel: 0, transitionPath: { type: TrajectoryFunctionType.LINEAR } },
    precision: DEFAULT_TARGET_PRECISION,
    transitionTargetProperties: {
      transitionElement: group!.linkedTransitionElement as TransitionElement,
    },
  };
  const node: ClientNode = {
    id: group.id,
    indicators: [indicatorId],
    actions: group.actionIds ?? [],
    boardId,
    title: group?.title ?? '',
    childNodes: [],
    _etag: '',
    _ts: 0,
    nodeProperties: {
      co2e: 0,
      data: [],
      description: '',
      diagramHeader: nodeStory?.diagramHeader,
      extendedDescription: {
        data: {
          texts: [{ body: nodeStory?.description ?? '', title: nodeStory?.title ?? '' }],
          images: [{ imgURL: prefixIfSet(nodeStory?.imgUrl), imgCredits: nodeStory?.imageCredits ?? '' }],
        },
        type: 1,
      },
      diff: 0,
      icon: getElementIconPath(group),
      imgURL: '',
      modelType: !!group?.linkedTransitionElement && !data.chartData ? ModelType.TRANSITION_ELEMENT : ModelType.CUSTOM,
      thumbnailURL: '',
      ...(group?.linkedTransitionElement
        ? { transitionElement: group?.linkedTransitionElement as TransitionElement }
        : {}),
      ...(group?.linkedTransitionElement && !!data.chartData ? { transitionTargetData } : {}),
      transitionTargetDescription: nodeStory?.description,
      customTargetModelData: dataWithRequiredChartData as CustomTargetModelData,
      type: NodeType.Target,
      historicalEmissionData,
      notAchievable: data.status === 'NOT_ACHIEVABLE',
    },
  };
  return { node, indicator };
};
